import React, { useState, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
} from '../ui'
import { useCreateEmployee, useUpdateEmployee, type Employee } from '../../hooks/useEmployees'
import { useDepartments } from '../../hooks/useDepartments'
import { usePositions } from '../../hooks/usePositions'
import { useWarehouses } from '../../hooks/useWarehouses'
import { useRoles } from '../../hooks/useRoles'
import { useUpdateUser } from '../../hooks/useApi'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingSpinner } from '../ui/loading-states'
import { InvitationCodeDisplay } from './InvitationCodeDisplay'

interface EmployeeFormData {
  employee_number: string
  first_name: string
  last_name: string
  middle_name?: string | null
  iin?: string | null
  email?: string | null
  phone?: string | null
  hire_date: string
  fire_date?: string | null
  department_id?: string | null
  position_id?: string | null
  warehouse_id?: string | null
  is_active: boolean
  is_materially_responsible: boolean
  contract_number?: string | null
  contract_type?: string | null
  create_invitation?: boolean
  invitation_role_id?: string | null
}

interface EmployeeFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee?: Employee | null
  mode: 'create' | 'edit'
}

export function EmployeeForm({ open, onOpenChange, employee, mode }: EmployeeFormProps) {
  const [formData, setFormData] = useState<EmployeeFormData>({
    employee_number: '',
    first_name: '',
    last_name: '',
    middle_name: null,
    iin: null,
    email: null,
    phone: null,
    hire_date: new Date().toISOString().split('T')[0],
    fire_date: null,
    department_id: null,
    position_id: null,
    warehouse_id: null,
    is_active: true,
    is_materially_responsible: false,
    contract_number: null,
    contract_type: null,
    create_invitation: false,
    invitation_role_id: null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Роль учётной записи (edit-режим, только если у сотрудника есть пользователь)
  const [roleId, setRoleId] = useState<string>('')
  const [createdInvitation, setCreatedInvitation] = useState<{
    id: string
    invite_code: string
    expires_at: Date
    is_valid: boolean
  } | null>(null)

  // Load departments, positions, warehouses and roles for selection
  const { data: departments } = useDepartments({ limit: 1000 })
  const { data: positions } = usePositions({ limit: 1000 })
  const { data: warehouses } = useWarehouses({ limit: 1000, is_active: true })
  const { data: roles } = useRoles({ limit: 100 })

  const createEmployee = useCreateEmployee()
  const updateEmployee = useUpdateEmployee()
  const updateUser = useUpdateUser()
  const queryClient = useQueryClient()

  const isLoading =
    createEmployee.isPending || updateEmployee.isPending || updateUser.isPending

  // Helper function to generate unique employee number
  const generateEmployeeNumber = () => {
    const now = new Date()
    const dateStr = now.getFullYear().toString().slice(-2) +
                   (now.getMonth() + 1).toString().padStart(2, '0') +
                   now.getDate().toString().padStart(2, '0')
    const timeStr = now.getHours().toString().padStart(2, '0') +
                   now.getMinutes().toString().padStart(2, '0')
    const randomStr = Math.floor(Math.random() * 100).toString().padStart(2, '0')
    return `${dateStr}${timeStr}${randomStr}`
  }

  // Initialize form data when dialog opens or employee changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && employee) {
        setFormData({
          employee_number: employee.employee_number || '',
          first_name: employee.first_name || '',
          last_name: employee.last_name || '',
          middle_name: employee.middle_name || null,
          iin: employee.iin || null,
          email: employee.email || null,
          phone: employee.phone || null,
          hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : new Date().toISOString().split('T')[0],
          fire_date: employee.fire_date ? employee.fire_date.split('T')[0] : null,
          department_id: employee.department_id || null,
          position_id: employee.position_id || null,
          warehouse_id: employee.warehouse_id || null,
          is_active: employee.is_active ?? true,
          is_materially_responsible: employee.is_materially_responsible ?? false,
          contract_number: employee.contract_number || null,
          contract_type: employee.contract_type || null,
        })
        setRoleId(employee.user?.role_id || employee.user?.role?.id || '')
      } else {
        setFormData({
          employee_number: generateEmployeeNumber(),
          first_name: '',
          last_name: '',
          middle_name: null,
          iin: null,
          email: null,
          phone: null,
          hire_date: new Date().toISOString().split('T')[0],
          fire_date: null,
          department_id: null,
          position_id: null,
          warehouse_id: null,
          is_active: true,
          is_materially_responsible: false,
          contract_number: null,
          contract_type: null,
          create_invitation: false,
          invitation_role_id: null,
        })
      }
      setErrors({})
      setCreatedInvitation(null) // Reset invitation state on open
    } else {
      setCreatedInvitation(null) // Reset invitation state on close
    }
  }, [open, mode, employee])

  const handleInputChange = (field: keyof EmployeeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value: string | boolean | null = e.target.value

    // Convert empty string to null for optional fields
    if (value === '' && (
      field === 'middle_name' ||
      field === 'iin' ||
      field === 'email' ||
      field === 'phone' ||
      field === 'fire_date' ||
      field === 'department_id' ||
      field === 'position_id' ||
      field === 'warehouse_id' ||
      field === 'contract_number' ||
      field === 'contract_type' ||
      field === 'invitation_role_id'
    )) {
      value = null
    }

    // Handle boolean fields
    if (field === 'is_active' || field === 'is_materially_responsible' || field === 'create_invitation') {
      value = e.target.value === 'true'
    }

    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }

    // Clear API errors when user changes critical fields that might cause conflicts
    if (field === 'employee_number' || field === 'iin' || field === 'email') {
      createEmployee.reset()
      updateEmployee.reset()
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.employee_number.trim()) {
      newErrors.employee_number = 'Табельный номер обязателен'
    } else if (formData.employee_number.trim().length < 1) {
      newErrors.employee_number = 'Табельный номер должен содержать минимум 1 символ'
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Имя обязательно'
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = 'Имя должно содержать минимум 2 символа'
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Фамилия обязательна'
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = 'Фамилия должна содержать минимум 2 символа'
    }

    if (!formData.hire_date) {
      newErrors.hire_date = 'Дата найма обязательна'
    }

    // Validate email format if provided
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Некорректный формат email'
      }
    }

    // Validate IIN format if provided (Kazakhstan format: 12 digits)
    if (formData.iin && formData.iin.trim() !== '') {
      const iinRegex = /^\d{12}$/
      if (!iinRegex.test(formData.iin.trim())) {
        newErrors.iin = 'ИИН должен содержать 12 цифр'
      }
    }

    // Validate invitation fields
    if (formData.create_invitation && !formData.invitation_role_id) {
      newErrors.invitation_role_id = 'Выберите роль для приглашения'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Prepare data for sending - ensure proper null/undefined handling
      // Helper function to safely convert values to null or string
      const toNullOrTrimmed = (value: string | null | undefined): string | null => {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return null;
        }
        return typeof value === 'string' ? value.trim() : String(value);
      };

      // Always send all fields to allow for clearing values
      const dataToSend: any = {
        employee_number: formData.employee_number.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        hire_date: formData.hire_date,
        is_active: formData.is_active,
        is_materially_responsible: formData.is_materially_responsible,
        // Always include all optional fields, even if null
        middle_name: toNullOrTrimmed(formData.middle_name),
        iin: toNullOrTrimmed(formData.iin),
        email: toNullOrTrimmed(formData.email),
        phone: toNullOrTrimmed(formData.phone),
        fire_date: toNullOrTrimmed(formData.fire_date),
        contract_number: toNullOrTrimmed(formData.contract_number),
        contract_type: toNullOrTrimmed(formData.contract_type),
        department_id: toNullOrTrimmed(formData.department_id),
        position_id: toNullOrTrimmed(formData.position_id),
        warehouse_id: toNullOrTrimmed(formData.warehouse_id),
      }

      // Add invitation fields only in create mode
      if (mode === 'create' && formData.create_invitation) {
        dataToSend.create_invitation = true
        dataToSend.invitation_role_id = formData.invitation_role_id
      }

      console.log('[EmployeeForm] formData:', formData)
      console.log('[EmployeeForm] dataToSend:', dataToSend)

      if (mode === 'create') {
        const result = await createEmployee.mutateAsync(dataToSend)
        // Check if invitation was created
        if (result.invitation) {
          setCreatedInvitation(result.invitation)
          // Don't close dialog yet - show invitation code
        } else {
          onOpenChange(false)
        }
      } else if (mode === 'edit' && employee) {
        console.log('[EmployeeForm] Sending PATCH request with:', { id: employee.id, data: dataToSend })
        await updateEmployee.mutateAsync({
          id: employee.id,
          data: dataToSend
        })

        // Смена роли учётной записи (если есть пользователь и роль изменилась)
        const currentRoleId =
          employee.user?.role_id || employee.user?.role?.id || ''
        if (employee.user_id && roleId && roleId !== currentRoleId) {
          await updateUser.mutateAsync({
            id: employee.user_id,
            data: { roleId },
          })
          await queryClient.invalidateQueries({ queryKey: ['employees'] })
        }

        onOpenChange(false)
      }
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {createdInvitation
              ? 'Сотрудник создан успешно!'
              : mode === 'create' ? 'Создать сотрудника' : 'Редактировать сотрудника'
            }
          </DialogTitle>
          <DialogDescription>
            {createdInvitation
              ? 'Скопируйте код приглашения и отправьте сотруднику.'
              : mode === 'create'
              ? 'Заполните информацию для создания нового сотрудника.'
              : 'Измените информацию о сотруднике.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        {createdInvitation ? (
          <div className="space-y-4 py-4">
            <InvitationCodeDisplay
              inviteCode={createdInvitation.invite_code}
              expiresAt={createdInvitation.expires_at}
              isValid={createdInvitation.is_valid}
            />
            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  setCreatedInvitation(null)
                  onOpenChange(false)
                }}
              >
                Закрыть
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Employee Number */}
            <div>
              <label htmlFor="employee_number" className="block text-sm font-medium text-gray-700 mb-1">
                Табельный номер *
              </label>
              <div className="flex gap-2">
                <Input
                  id="employee_number"
                  type="text"
                  value={formData.employee_number}
                  onChange={handleInputChange('employee_number')}
                  placeholder="Введите табельный номер"
                  disabled={isLoading}
                  className={errors.employee_number ? 'border-red-500' : ''}
                />
                {mode === 'create' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, employee_number: generateEmployeeNumber() }))
                      createEmployee.reset()
                    }}
                    disabled={isLoading}
                    title="Сгенерировать новый номер"
                  >
                    🔄
                  </Button>
                )}
              </div>
              {errors.employee_number && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.employee_number}
                </div>
              )}
            </div>

            {/* IIN */}
            <div>
              <label htmlFor="iin" className="block text-sm font-medium text-gray-700 mb-1">
                ИИН
              </label>
              <Input
                id="iin"
                type="text"
                value={formData.iin || ''}
                onChange={handleInputChange('iin')}
                placeholder="123456789012"
                maxLength={12}
                disabled={isLoading}
                className={errors.iin ? 'border-red-500' : ''}
              />
              {errors.iin && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.iin}
                </div>
              )}
            </div>
          </div>

          {/* Full Name */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Фамилия *
              </label>
              <Input
                id="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleInputChange('last_name')}
                placeholder="Введите фамилию"
                disabled={isLoading}
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.last_name}
                </div>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                Имя *
              </label>
              <Input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                placeholder="Введите имя"
                disabled={isLoading}
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.first_name}
                </div>
              )}
            </div>

            {/* Middle Name */}
            <div>
              <label htmlFor="middle_name" className="block text-sm font-medium text-gray-700 mb-1">
                Отчество
              </label>
              <Input
                id="middle_name"
                type="text"
                value={formData.middle_name || ''}
                onChange={handleInputChange('middle_name')}
                placeholder="Введите отчество"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={handleInputChange('email')}
                placeholder="example@company.com"
                disabled={isLoading}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.email}
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Телефон
              </label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={handleInputChange('phone')}
                placeholder="+7 (777) 123-45-67"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Organization */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Department */}
            <div>
              <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
                Подразделение
              </label>
              <Select
                id="department_id"
                value={formData.department_id || ''}
                onChange={handleInputChange('department_id')}
                disabled={isLoading}
              >
                <SelectOption value="">Не назначено</SelectOption>
                {departments?.data?.map(dept => (
                  <SelectOption key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectOption>
                ))}
              </Select>
            </div>

            {/* Position */}
            <div>
              <label htmlFor="position_id" className="block text-sm font-medium text-gray-700 mb-1">
                Должность
              </label>
              <Select
                id="position_id"
                value={formData.position_id || ''}
                onChange={handleInputChange('position_id')}
                disabled={isLoading}
              >
                <SelectOption value="">Не назначено</SelectOption>
                {positions?.data?.map(position => (
                  <SelectOption key={position.id} value={position.id}>
                    {position.name}
                  </SelectOption>
                ))}
              </Select>
            </div>

            {/* Warehouse */}
            <div>
              <label htmlFor="warehouse_id" className="block text-sm font-medium text-gray-700 mb-1">
                Склад
              </label>
              <Select
                id="warehouse_id"
                value={formData.warehouse_id || ''}
                onChange={handleInputChange('warehouse_id')}
                disabled={isLoading}
              >
                <SelectOption value="">Не назначен</SelectOption>
                {warehouses?.data?.map(warehouse => (
                  <SelectOption key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </SelectOption>
                ))}
              </Select>
            </div>
          </div>

          {/* Employment Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hire Date */}
            <div>
              <label htmlFor="hire_date" className="block text-sm font-medium text-gray-700 mb-1">
                Дата найма *
              </label>
              <Input
                id="hire_date"
                type="date"
                value={formData.hire_date}
                onChange={handleInputChange('hire_date')}
                disabled={isLoading}
                className={errors.hire_date ? 'border-red-500' : ''}
              />
              {errors.hire_date && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.hire_date}
                </div>
              )}
            </div>

            {/* Fire Date */}
            <div>
              <label htmlFor="fire_date" className="block text-sm font-medium text-gray-700 mb-1">
                Дата увольнения
              </label>
              <Input
                id="fire_date"
                type="date"
                value={formData.fire_date || ''}
                onChange={handleInputChange('fire_date')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Contract Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Contract Number */}
            <div>
              <label htmlFor="contract_number" className="block text-sm font-medium text-gray-700 mb-1">
                Номер договора
              </label>
              <Input
                id="contract_number"
                type="text"
                value={formData.contract_number || ''}
                onChange={handleInputChange('contract_number')}
                placeholder="№ договора"
                disabled={isLoading}
              />
            </div>

            {/* Contract Type */}
            <div>
              <label htmlFor="contract_type" className="block text-sm font-medium text-gray-700 mb-1">
                Тип договора
              </label>
              <Select
                id="contract_type"
                value={formData.contract_type || ''}
                onChange={handleInputChange('contract_type')}
                disabled={isLoading}
              >
                <SelectOption value="">Не указан</SelectOption>
                <SelectOption value="full_time">Полная занятость</SelectOption>
                <SelectOption value="part_time">Частичная занятость</SelectOption>
                <SelectOption value="contract">Подрядный</SelectOption>
                <SelectOption value="internship">Стажировка</SelectOption>
              </Select>
            </div>
          </div>

          {/* Schedule Template (read-only, edit mode only) */}
          {mode === 'edit' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                График работы
              </label>
              <Input
                type="text"
                value={employee?.schedule_template?.name || 'Не назначен'}
                disabled
                className="bg-muted cursor-not-allowed"
              />
            </div>
          )}

          {/* Роль учётной записи (edit-режим) */}
          {mode === 'edit' && (
            <div>
              <label htmlFor="account_role" className="block text-sm font-medium text-gray-700 mb-1">
                Роль (доступ в системе)
              </label>
              {employee?.user_id ? (
                <>
                  <Select
                    id="account_role"
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    disabled={isLoading}
                  >
                    <SelectOption value="">— Не выбрана —</SelectOption>
                    {roles?.data?.map((role) => (
                      <SelectOption key={role.id} value={role.id}>
                        {role.name}
                      </SelectOption>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Определяет, какие разделы доступны пользователю.
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  У сотрудника ещё нет учётной записи — роль назначается при
                  регистрации по приглашению.
                </p>
              )}
            </div>
          )}

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="is_active" className="block text-sm font-medium text-gray-700 mb-1">
                Статус
              </label>
              <Select
                id="is_active"
                value={formData.is_active.toString()}
                onChange={handleInputChange('is_active')}
                disabled={isLoading}
              >
                <SelectOption value="true">Активный</SelectOption>
                <SelectOption value="false">Неактивный</SelectOption>
              </Select>
            </div>

            <div>
              <label htmlFor="is_materially_responsible" className="block text-sm font-medium text-gray-700 mb-1">
                Материальная ответственность
              </label>
              <Select
                id="is_materially_responsible"
                value={formData.is_materially_responsible.toString()}
                onChange={handleInputChange('is_materially_responsible')}
                disabled={isLoading}
              >
                <SelectOption value="false">Не ответственный</SelectOption>
                <SelectOption value="true">Ответственный за ТМЦ</SelectOption>
              </Select>
            </div>
          </div>

          {/* Invitation Section */}
          {mode === 'create' && (
            <>
              {/* Create Invitation Checkbox */}
              <div className="border-t pt-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.create_invitation || false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        create_invitation: e.target.checked,
                        // Reset role if unchecking
                        invitation_role_id: e.target.checked ? prev.invitation_role_id : null
                      }))
                    }}
                    disabled={isLoading}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Создать приглашение для сотрудника
                  </span>
                </label>
                <p className="mt-1 text-sm text-gray-500 ml-6">
                  Автоматически создать код приглашения для регистрации в мобильном приложении
                </p>
              </div>

              {/* Role Selection - Conditional */}
              {formData.create_invitation && (
                <div>
                  <label htmlFor="invitation_role_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Роль для приглашения *
                  </label>
                  <Select
                    id="invitation_role_id"
                    value={formData.invitation_role_id || ''}
                    onChange={handleInputChange('invitation_role_id')}
                    disabled={isLoading}
                    className={errors.invitation_role_id ? 'border-red-500' : ''}
                  >
                    <SelectOption value="">Выберите роль</SelectOption>
                    {roles?.data?.map(role => (
                      <SelectOption key={role.id} value={role.id}>
                        {role.name}
                      </SelectOption>
                    ))}
                  </Select>
                  {errors.invitation_role_id && (
                    <div className="flex items-center mt-1 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.invitation_role_id}
                    </div>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Роль будет назначена пользователю после регистрации по приглашению
                  </p>
                </div>
              )}
            </>
          )}

          {/* Show existing invitation in edit mode */}
          {mode === 'edit' && employee?.invitation && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Приглашение сотрудника
              </h3>
              <InvitationCodeDisplay
                inviteCode={employee.invitation.invite_code}
                expiresAt={employee.invitation.expires_at}
                isValid={employee.invitation.is_valid}
              />
            </div>
          )}

          {/* Show API errors */}
          {(createEmployee.error || updateEmployee.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = (createEmployee.error || updateEmployee.error) as any
                  const message = error?.message
                  const status = error?.status

                  if (message === 'Employee with this number already exists') {
                    return 'Сотрудник с таким табельным номером уже существует. Выберите другой номер.'
                  } else if (message === 'Employee with this IIN already exists') {
                    return 'Сотрудник с таким ИИН уже существует. Проверьте правильность ввода.'
                  } else if (message === 'Employee with this email already exists') {
                    return 'Сотрудник с таким email уже существует. Используйте другой адрес.'
                  } else if (status === 400) {
                    return 'Проверьте правильность заполнения полей формы.'
                  } else if (status === 403) {
                    return 'У вас нет прав для выполнения этого действия.'
                  } else if (status === 500) {
                    return 'Произошла ошибка на сервере. Попробуйте позже.'
                  } else {
                    return message || 'Произошла ошибка при сохранении'
                  }
                })()}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}